import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {StyleType} from "@catui/types";
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {GenericResourceService} from "@catfeatures/services/generic-resource.service";
import {NotificationsService} from "@catcore/services";
import {ActiveModal} from "@catmodules/widgets/slide-in-modal/utils/modal-ref";
import {EntitySchemaService} from "@catcore/services/entity-schema.service";
import {EntitySchemaType} from "@catcore/utils/json-schema/entity-schema-to-json-schema";
import {buildFormControlsFromSchema} from "@catuiform/shared/form-builder";
import {isNullOrUndefined} from "util";
import {sortByLabelFunc} from "@catcore/utils";
import {AudioInputSignalService} from "@catmodels/project-speaker-setup/services/audio-input-signal.service";
import {Observable, Subscription} from "rxjs";
import {Parameter} from "@catmodels/parameters/types";
import {ProjectSpeakerPortService} from "@catmodels/project-speaker-setup/services/project-speaker-port.service";

@Component({
  selector: 'catapp-modify-input-channels',
  templateUrl: './modify-input-channels.component.html',
  styleUrls: ['./modify-input-channels.component.scss']
})
export class ModifyInputChannelsComponent implements OnInit {

  styleType = StyleType;
  form: FormGroup;
  formArray: FormArray;
  guiZoneOptions = [];

  channelOptions = [
    {value: 'leftFront', label: 'Left Front'},
    {value: 'rightFront', label: 'Right Front'},
    {value: 'center', label: 'Center'},
    {value: 'leftRear', label: 'Left Rear'},
    {value: 'rightRear', label: 'Right Rear'},
    {value: 'other', label: 'Sub'},
  ];

  @Output() afterUpdate = new EventEmitter<any>();

  private _speakerPort: any;
  private _inputChannels: any = [];
  private _guiZones: any = [];

  private _removedIds = [];

  private modelSchema;
  private inputChannelSubscription: Subscription;

  constructor(private formBuilder: FormBuilder,
              private resourceService: GenericResourceService,
              private notification: NotificationsService,
              private activeModal: ActiveModal,
              private audioInputMixerService: AudioInputSignalService,
              private projectSpeakerPortService: ProjectSpeakerPortService) {
    this.formArray = this.formBuilder.array([]);
    this.form = this.formBuilder.group({
      volume_control_channel: ['', []],
      dac_range: ['', []],
      pga_gain: ['', []],
      input_signals: this.formArray
    })
  }

  @Input()
  set guiZones(guiZones) {
    this._guiZones = guiZones;
    this.guiZoneOptions = [];
    this._guiZones.forEach((guiZone) => {
      this.guiZoneOptions.push({
        value: guiZone._links.self.href,
        label: guiZone.name
      })
    });
    this.guiZoneOptions.sort(sortByLabelFunc);
  }

  @Input()
  get inputChannels(): any {
    return this._inputChannels;
  }

  set inputChannels(inputChannels: any) {
    this._inputChannels = inputChannels;
    console.log('Input channels', this._inputChannels);
    this.updateInputChannels();
  }

  @Input()
  get speakerPort(): any {
    return this._speakerPort;
  }

  set speakerPort(value: any) {
    this._speakerPort = value;
    this.form.patchValue(this._speakerPort);

    if (this._speakerPort.volume_control_channel === null) {
      this.form.patchValue({volume_control_channel: this.channelOptions[0].value});
      this.form.controls['volume_control_channel'].markAsDirty();
    }

    this.inputChannelSubscription = this.projectSpeakerPortService
      .getInputChannels(this._speakerPort)
      .subscribe((item) => {
        this._inputChannels = item;
        this.updateInputChannels();
      });
  }

  addFormArrayItem(initial: boolean = false) {
    const formControl = this.formBuilder.group({
      speaker_port: this.speakerPort._links.self.href,
      id: null,
      gui_zone: [this.speakerPort._links.gui_zone.href, [Validators.required]],
      channel: [this.channelOptions[0].value, []],
      gain_value: [0, []]
    });
    this.formArray.push(formControl);
    if (!initial) {
      formControl.markAsDirty();
    }
    return formControl;
  }

  removeFormArrayItem() {
    if (this.formArray.length > 1) {
      const idx = this.formArray.length - 1;
      if (this.formArray.at(idx).value.id !== null) {
        this._removedIds.push(this.formArray.at(idx).value.id);
      }
      this.formArray.removeAt(this.formArray.length - 1);
      this.formArray.markAsDirty();
    }
  }

  updateInputChannels() {
    while (this.formArray.length > 0) {
      this.formArray.removeAt(0);
    }
    this._removedIds = [];
    if (this._speakerPort
        && !isNullOrUndefined(this._speakerPort.id)
        && this._inputChannels
        && this._inputChannels.length > 0) {
      this._inputChannels.forEach((inputChannel) => {
        const formControl = this.addFormArrayItem(true);
        formControl.get('id').setValue(inputChannel.id);
        formControl.get('gui_zone').setValue(inputChannel._links.gui_zone.href);
        formControl.get('channel').setValue(inputChannel.channel);
        formControl.get('gain_value').setValue(inputChannel.gain_value);
      });
    }
  }

  onSubmit() {
    const requests = [];
    this._removedIds.forEach((idx) => {
      const inputChannel = this._inputChannels.find((ic) => ic.id === idx);
      if (inputChannel !== undefined) {
        requests.push(this.audioInputMixerService.delete(inputChannel));
      }
    });

    this.formArray.controls.forEach((control) => {
      if (control.dirty) {
        if (control.value.id === null) {
          requests.push(this.audioInputMixerService.add(control.value));
        } else {
          requests.push(this.audioInputMixerService.update(control.value));
        }
      }
    });

    if (this.form.get('volume_control_channel').dirty
        || this.form.get('dac_range').dirty
        || this.form.get('pga_gain').dirty) {
      requests.push(this.resourceService.findServiceForResource(this._speakerPort._links.self.href).save({
        volume_control_channel: this.form.value.volume_control_channel,
        dac_range: this.form.value.dac_range,
        pga_gain: this.form.value.pga_gain
      }));
    }

    Observable.forkJoin(requests)
      .subscribe((results) => {
        this.notification.success('Update successful', 'Successfully updated ' + results.length + ' input channels');
        this.afterUpdate.emit(results);
        this.form.markAsPristine();
        this.activeModal.close();
      })

  }

  ngOnInit() {
  }

}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/models/project-speaker-setup/components/modify-input-channels/modify-input-channels.component.ts